import { Languages } from 'lucide-react'
import { LocaleFlag } from '@/components/LocaleFlag'
import { useAuth } from '@/context/AuthContext'
import { useI18n } from '@/i18n/I18nProvider'
import { isLocale, LOCALES, type Locale } from '@/i18n/locale'
import { useMeta } from '@/lib/queries'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui'

/**
 * Names for the four languages, each written in its own.
 *
 * The list the switcher actually renders comes from `/api/meta`, because the
 * set of languages the server can answer in is the server's fact and a client
 * with its own copy will eventually offer one the API cannot produce. This is
 * the fallback for the one screen where that matters most and the meta call has
 * not landed yet — sign-in, on a cold cache, which is the first thing anybody
 * sees.
 *
 * Endonyms in both places. A person whose interface is currently in a language
 * they cannot read is exactly the one who needs to find theirs in this list, and
 * "Bosanski" is findable to them in a way "Bosnian" is not.
 */
const FALLBACK_NAMES: Record<Locale, string> = {
  bs: 'Bosanski',
  hr: 'Hrvatski',
  sr: 'Srpski',
  en: 'English',
}

/**
 * The two-letter code, kept beside the flag rather than replaced by it.
 *
 * Flags carry a claim that is not quite true here, and the code is what keeps
 * the control honest. Bosnian, Croatian and Serbian are three languages spoken
 * *in Bosnia*, by people who live here — not three countries' languages — and
 * English has no flag at all. So the flag is the thing you recognise at a
 * glance and the code is the thing that is actually correct: ISO 639-1, which
 * is what a language picker indexes.
 *
 * It also does real work when the flag does not. Four small rectangles are hard
 * to tell apart at a glance, and Serbia and Croatia share three colours in a
 * different order — the code resolves that without anybody squinting.
 */
const CODES: Record<Locale, string> = {
  bs: 'BS',
  hr: 'HR',
  sr: 'SR',
  en: 'EN',
}

export function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, t } = useI18n()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="ghost" className={className} aria-label={t('language.change')} />
        }
      >
        <Languages className="size-5 lg:hidden" aria-hidden />
        {/* The flag replaces the generic icon where there is room for it; on a
            narrow header the icon is the more legible of the two. */}
        <LocaleFlag locale={locale} className="hidden lg:inline-block" />
        {/*
          The current code, beside the icon rather than instead of it.
          The icon alone says "language" to anybody, in any language, which is
          exactly the person this control exists for; the code says which one is
          on without making them open the menu to find out.
        */}
        {/*
          Hidden on the narrowest screens.

          The trigger is already an icon that says "language" in any language,
          which is the whole point of it; the code is a nicety that costs about
          22px, and on a 390px header those 22px were part of what pushed the
          controls over the brand name.
        */}
        <span aria-hidden className="hidden text-[0.6875rem] font-semibold tracking-wide sm:inline">
          {CODES[locale]}
        </span>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <LanguageRadioGroup />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The four languages as radio rows — the body of the switcher's menu.
 *
 * Exported on its own so the account menu can carry the same list in a
 * submenu: once language moved out of the header and into the avatar menu,
 * a second copy of the options and the save rule would have been the one to
 * forget the `persistToAccount` distinction below.
 */
export function LanguageRadioGroup() {
  const { locale, setLocale } = useI18n()
  const { user } = useAuth()
  // Guests get the fallback list below rather than a 401 — see useMeta.
  const { data: meta } = useMeta({ enabled: Boolean(user) })

  /*
   * Filtered against `LOCALES` rather than rendered straight from the server.
   *
   * A language the API offers but this bundle has no dictionary for would
   * render its own chrome as raw keys — `login.submit` on the sign-in button —
   * which is a worse outcome than not offering it for the one deploy it takes
   * for the two to line up.
   */
  const options =
    meta?.locales
      ?.filter((option) => isLocale(option.value))
      .map((option) => ({ value: option.value as Locale, label: option.label })) ??
    LOCALES.map((value) => ({ value, label: FALLBACK_NAMES[value] }))

  return (
    <DropdownMenuRadioGroup
      value={locale}
      onValueChange={(value) => {
        if (!isLocale(value)) return

        /*
         * Saved to the account only when there is one to save it to.
         *
         * On the sign-in screen there is no session, so the choice lives in
         * this browser until somebody signs in. It still reaches the server
         * for the request at hand — the header carries it — which is what
         * makes a failed sign-in come back in the language on screen.
         */
        void setLocale(value, { persistToAccount: Boolean(user) })
      }}
    >
      {options.map((option) => (
        <DropdownMenuRadioItem key={option.value} value={option.value}>
          {/*
            Fixed width and tabular figures so the four names line up in a
            column rather than each starting wherever its code happens to
            end. `aria-hidden` because the name beside it already says which
            language this is — a screen reader announcing "BS Bosanski"
            reads the same thing twice.
          */}
          <LocaleFlag locale={option.value} className="mr-2 shrink-0" />
          <span
            aria-hidden
            className="text-muted-foreground mr-2 inline-block w-6 text-[0.6875rem] font-semibold tracking-wide tabular-nums"
          >
            {CODES[option.value]}
          </span>
          {option.label}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  )
}
